import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import CookieBot from 'react-cookiebot';

declare global {
  interface Window {
    Cookiebot?: {
      consent: {
        necessary: boolean;
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
      consented: boolean;
      consentID: string;
      consentUTC: string;
      withdraw: () => void;
      renew: () => void;
      show: () => void;
      submitCustomConsent: (
        preferences: boolean,
        statistics: boolean,
        marketing: boolean,
      ) => void;
    };
  }
}

export interface ConsentState {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

interface ConsentDetails {
  consentId: string;
  consentDate: string;
}

interface CookieConsentContextType {
  consent: ConsentState;
  consentDetails: ConsentDetails | null;
  isConsentReady: boolean;
  hasStatisticsConsent: boolean;
  hasMarketingConsent: boolean;
  isCookiebotEnabled: boolean;
  showConsentBanner: () => void;
  withdrawConsent: () => void;
  submitConsent: (consent: Omit<ConsentState, 'necessary'>) => void;
}

const defaultConsent: ConsentState = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
};

const CookieConsentContext = createContext<CookieConsentContextType>({
  consent: defaultConsent,
  consentDetails: null,
  isConsentReady: false,
  hasStatisticsConsent: false,
  hasMarketingConsent: false,
  isCookiebotEnabled: false,
  showConsentBanner: () => {},
  withdrawConsent: () => {},
  submitConsent: () => {},
});

interface CookieConsentProviderProps {
  children: React.ReactNode;
  domainGroupId?: string;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
  domainGroupId,
}) => {
  const [consent, setConsent] = useState<ConsentState>(defaultConsent);
  const [consentDetails, setConsentDetails] = useState<ConsentDetails | null>(
    null,
  );
  const [isConsentReady, setIsConsentReady] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(() => {
    return (
      typeof document !== 'undefined' &&
      document.getElementById('CookieBot') !== null
    );
  });

  const isCookiebotEnabled =
    process.env.NEXT_PUBLIC_COOKIEBOT_ENABLED === 'true';
  const cookiebotId =
    domainGroupId || process.env.NEXT_PUBLIC_COOKIEBOT_DOMAIN_GROUP_ID;

  // Mark script as loaded after first render to prevent duplicate script injection
  useEffect(() => {
    if (cookiebotId && !scriptLoaded) {
      setScriptLoaded(true);
    }
  }, [cookiebotId, scriptLoaded]);

  // When Cookiebot is disabled, immediately set consent ready with all tracking enabled
  useEffect(() => {
    if (!isCookiebotEnabled) {
      setConsent({
        necessary: true,
        preferences: true,
        statistics: true,
        marketing: true,
      });
      setIsConsentReady(true);
    }
  }, [isCookiebotEnabled]);

  const updateConsentFromCookiebot = useCallback(() => {
    if (window.Cookiebot?.consent) {
      const newConsent: ConsentState = {
        necessary: window.Cookiebot.consent.necessary ?? true,
        preferences: window.Cookiebot.consent.preferences ?? false,
        statistics: window.Cookiebot.consent.statistics ?? false,
        marketing: window.Cookiebot.consent.marketing ?? false,
      };
      setConsent(newConsent);
      setIsConsentReady(true);

      if (window.Cookiebot.consented) {
        setConsentDetails({
          consentId: window.Cookiebot.consentID,
          consentDate: window.Cookiebot.consentUTC,
        });
      }
    }
  }, []);

  useEffect(() => {
    // Listen for Cookiebot consent events
    const handleConsentReady = () => {
      updateConsentFromCookiebot();
    };

    const handleAccept = () => {
      updateConsentFromCookiebot();
    };

    const handleDecline = () => {
      updateConsentFromCookiebot();
    };

    window.addEventListener('CookiebotOnConsentReady', handleConsentReady);
    window.addEventListener('CookiebotOnAccept', handleAccept);
    window.addEventListener('CookiebotOnDecline', handleDecline);

    // Check if Cookiebot is already loaded
    if (window.Cookiebot?.consent) {
      updateConsentFromCookiebot();
    }

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', handleConsentReady);
      window.removeEventListener('CookiebotOnAccept', handleAccept);
      window.removeEventListener('CookiebotOnDecline', handleDecline);
    };
  }, [updateConsentFromCookiebot]);

  const showConsentBanner = useCallback(() => {
    window.Cookiebot?.show();
  }, []);

  const withdrawConsent = useCallback(() => {
    window.Cookiebot?.withdraw();
    setConsent(defaultConsent);
    setConsentDetails(null);
  }, []);

  const submitConsent = useCallback(
    (newConsent: Omit<ConsentState, 'necessary'>) => {
      window.Cookiebot?.submitCustomConsent(
        newConsent.preferences,
        newConsent.statistics,
        newConsent.marketing,
      );
    },
    [],
  );

  const value: CookieConsentContextType = {
    consent,
    consentDetails,
    isConsentReady,
    hasStatisticsConsent: consent.statistics,
    hasMarketingConsent: consent.marketing,
    isCookiebotEnabled,
    showConsentBanner,
    withdrawConsent,
    submitConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {isCookiebotEnabled && cookiebotId && !scriptLoaded && (
        <CookieBot domainGroupId={cookiebotId} />
      )}
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};
